// src/pages/notifications/ChannelCreatePage.tsx
// Channel create — desktop-app create dialog (docs/ux/14-forms.md), built on
// the form-kit (FormDialog/TitleInput/AutoGrowTextarea) from src/components/form.
// Route-hosted (kept for direct links from ChannelListPage's "+ New") — the
// dialog opens over the list on mount and closing returns to it. Type-specific
// config fields render below the common fields since they depend on `type`.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsChannel, useI18n } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea, SegmentedControl } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { Select } from '@/components/Select';
import { Input, Alert } from '@react/components';
import { validateChannelCreate, formatChannelType, formatChannelScope } from '@/features/notifications/logic';
import { NotificationChannelTypeValues } from '@core/types/notifications/NotificationChannelType';
import { ChannelScopeValues } from '@core/types/notifications/ChannelScope';
import type { ChannelCreateFormState } from '@/features/notifications/types';

const initialForm: ChannelCreateFormState = {
  name: '', description: '', type: 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED', scope: 'CHANNEL_SCOPE_TENANT',
  isDefault: false, isEnabled: true,
  smtpHost: '', smtpPort: '587', smtpUsername: '', smtpPassword: '', useTls: true, fromEmail: '', fromName: '', replyTo: '',
  smsProvider: '', smsApiKey: '', smsApiSecret: '', smsSenderId: '',
  telegramBotToken: '', telegramWebhookUrl: '', telegramUseWebhook: false,
  slackBotToken: '', slackSigningSecret: '', slackDefaultChannel: '', slackWebhookUrl: '', slackUseBotToken: true,
  pushProvider: '', pushFcmServerKey: '', pushFcmProjectId: '', pushApnsKeyId: '', pushApnsTeamId: '', pushApnsBundleId: '', pushApnsProduction: false,
  webhookUrl: '', webhookMethod: 'POST', webhookSecretToken: '', webhookContentType: 'application/json', webhookVerifySsl: true,
};

const scopeOptions = ChannelScopeValues.filter((v) => v !== 'CHANNEL_SCOPE_UNSPECIFIED').map((s) => {
  const sc = formatChannelScope(s);
  return { value: s, labelKey: sc.labelKey };
});

const webhookMethodOptions = [
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
];

export const ChannelCreatePage = observer(() => {
  const store = useNotificationsChannel();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState<ChannelCreateFormState>({ ...initialForm });
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const [validationResult, setValidationResult] = useState<{ valid: boolean; message?: string } | null>(null);
  const [validating, setValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const close = () => navigate('/notifications/channels');

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const dirty = !!(form.name.trim() || form.description.trim());

  const buildConfig = () => {
    switch (form.type) {
      case 'NOTIFICATION_CHANNEL_TYPE_EMAIL':
        return { email_config: { smtp_host: form.smtpHost, smtp_port: parseInt(form.smtpPort) || 587, username: form.smtpUsername, password: form.smtpPassword, use_tls: form.useTls, from_email: form.fromEmail, from_name: form.fromName, reply_to: form.replyTo } };
      case 'NOTIFICATION_CHANNEL_TYPE_SMS':
        return { sms_config: { provider: form.smsProvider, api_key: form.smsApiKey, api_secret: form.smsApiSecret, sender_id: form.smsSenderId } };
      case 'NOTIFICATION_CHANNEL_TYPE_TELEGRAM':
        return { telegram_config: { bot_token: form.telegramBotToken, webhook_url: form.telegramWebhookUrl, use_webhook: form.telegramUseWebhook } };
      case 'NOTIFICATION_CHANNEL_TYPE_SLACK':
        return { slack_config: { bot_token: form.slackBotToken, signing_secret: form.slackSigningSecret, default_channel: form.slackDefaultChannel, webhook_url: form.slackWebhookUrl, use_bot_token: form.slackUseBotToken } };
      case 'NOTIFICATION_CHANNEL_TYPE_PUSH':
        return { push_config: { provider: form.pushProvider, fcm_server_key: form.pushFcmServerKey, fcm_project_id: form.pushFcmProjectId, apns_key_id: form.pushApnsKeyId, apns_team_id: form.pushApnsTeamId, apns_bundle_id: form.pushApnsBundleId, apns_production: form.pushApnsProduction } };
      case 'NOTIFICATION_CHANNEL_TYPE_WEBHOOK':
        return { webhook_config: { url: form.webhookUrl, method: form.webhookMethod, secret_token: form.webhookSecretToken, content_type: form.webhookContentType, verify_ssl: form.webhookVerifySsl } };
      default:
        return {};
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    setValidationResult(null);
    try {
      const resp = await store.ValidateChannelConfig({ type: form.type, ...buildConfig() } as never);
      const r = resp as { is_valid?: boolean; error_message?: string } | null;
      setValidationResult({ valid: r?.is_valid ?? false, message: r?.error_message });
    } catch (err) {
      setValidationResult({ valid: false, message: err instanceof Error ? err.message : t('errors.unknown') });
    } finally {
      setValidating(false);
    }
  };

  const submit = async () => {
    const validation = validateChannelCreate(form);
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }
    setIsSubmitting(true);
    try {
      await store.CreateChannel({
        entity: {} as never,
        name: form.name.trim(),
        description: form.description.trim(),
        type: form.type,
        scope: form.scope,
        is_default: form.isDefault,
        is_enabled: form.isEnabled,
        ...buildConfig(),
      } as never);
      close();
    } catch (err) {
      setErrors({ form: err });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTypeConfig = () => {
    switch (form.type) {
      case 'NOTIFICATION_CHANNEL_TYPE_EMAIL':
        return (
          <>
            <Input label={t('notifications.smtpHost')} value={form.smtpHost} onChange={handleChange('smtpHost')} error={errors.smtpHost as string} required />
            <Input label={t('notifications.smtpPort')} type="number" value={form.smtpPort} onChange={handleChange('smtpPort')} />
            <Input label={t('notifications.smtpUsername')} value={form.smtpUsername} onChange={handleChange('smtpUsername')} />
            <Input label={t('notifications.smtpPassword')} type="password" value={form.smtpPassword} onChange={handleChange('smtpPassword')} />
            <ToggleSwitch label={t('notifications.useTls')} checked={form.useTls} onChange={(v) => setForm((p) => ({ ...p, useTls: v }))} />
            <Input label={t('notifications.fromEmail')} type="email" value={form.fromEmail} onChange={handleChange('fromEmail')} error={errors.fromEmail as string} required />
            <Input label={t('notifications.fromName')} value={form.fromName} onChange={handleChange('fromName')} />
            <Input label={t('notifications.replyTo')} type="email" value={form.replyTo} onChange={handleChange('replyTo')} />
          </>
        );
      case 'NOTIFICATION_CHANNEL_TYPE_SMS':
        return (
          <>
            <Input label={t('notifications.smsApiKey')} value={form.smsApiKey} onChange={handleChange('smsApiKey')} />
            <Input label={t('notifications.smsApiSecret')} type="password" value={form.smsApiSecret} onChange={handleChange('smsApiSecret')} />
            <Input label={t('notifications.smsSenderId')} value={form.smsSenderId} onChange={handleChange('smsSenderId')} />
          </>
        );
      case 'NOTIFICATION_CHANNEL_TYPE_TELEGRAM':
        return (
          <>
            <Input label={t('notifications.botToken')} value={form.telegramBotToken} onChange={handleChange('telegramBotToken')} error={errors.telegramBotToken as string} required />
            <Input label={t('notifications.webhookUrl')} value={form.telegramWebhookUrl} onChange={handleChange('telegramWebhookUrl')} />
            <ToggleSwitch label={t('notifications.useWebhook')} checked={form.telegramUseWebhook} onChange={(v) => setForm((p) => ({ ...p, telegramUseWebhook: v }))} />
          </>
        );
      case 'NOTIFICATION_CHANNEL_TYPE_SLACK':
        return (
          <>
            <ToggleSwitch label={t('notifications.slackUseBotToken')} checked={form.slackUseBotToken} onChange={(v) => setForm((p) => ({ ...p, slackUseBotToken: v }))} />
            {form.slackUseBotToken ? (
              <>
                <Input label={t('notifications.slackBotToken')} value={form.slackBotToken} onChange={handleChange('slackBotToken')} error={errors.slackBotToken as string} />
                <Input label={t('notifications.slackSigningSecret')} type="password" value={form.slackSigningSecret} onChange={handleChange('slackSigningSecret')} />
                <Input label={t('notifications.slackDefaultChannel')} value={form.slackDefaultChannel} onChange={handleChange('slackDefaultChannel')} />
              </>
            ) : (
              <Input label={t('notifications.slackWebhookUrl')} value={form.slackWebhookUrl} onChange={handleChange('slackWebhookUrl')} error={errors.slackBotToken as string} />
            )}
          </>
        );
      case 'NOTIFICATION_CHANNEL_TYPE_PUSH':
        return (
          <>
            <Input label={t('notifications.pushFcmServerKey')} value={form.pushFcmServerKey} onChange={handleChange('pushFcmServerKey')} />
            <Input label={t('notifications.pushFcmProjectId')} value={form.pushFcmProjectId} onChange={handleChange('pushFcmProjectId')} />
            <Input label={t('notifications.pushApnsKeyId')} value={form.pushApnsKeyId} onChange={handleChange('pushApnsKeyId')} />
            <Input label={t('notifications.pushApnsTeamId')} value={form.pushApnsTeamId} onChange={handleChange('pushApnsTeamId')} />
            <Input label={t('notifications.pushApnsBundleId')} value={form.pushApnsBundleId} onChange={handleChange('pushApnsBundleId')} />
            <ToggleSwitch label={t('notifications.pushApnsProduction')} checked={form.pushApnsProduction} onChange={(v) => setForm((p) => ({ ...p, pushApnsProduction: v }))} />
          </>
        );
      case 'NOTIFICATION_CHANNEL_TYPE_WEBHOOK':
        return (
          <>
            <Input label={t('notifications.webhookUrl')} value={form.webhookUrl} onChange={handleChange('webhookUrl')} error={errors.webhookUrl as string} required />
            <div>
              <label className="label">{t('notifications.webhookMethod')}</label>
              <SegmentedControl
                aria-label={t('notifications.webhookMethod')}
                value={form.webhookMethod}
                onChange={(v) => setForm((p) => ({ ...p, webhookMethod: v }))}
                options={webhookMethodOptions}
              />
            </div>
            <Input label={t('notifications.webhookSecretToken')} type="password" value={form.webhookSecretToken} onChange={handleChange('webhookSecretToken')} />
            <Input label={t('notifications.webhookContentType')} value={form.webhookContentType} onChange={handleChange('webhookContentType')} />
            <ToggleSwitch label={t('notifications.webhookVerifySsl')} checked={form.webhookVerifySsl} onChange={(v) => setForm((p) => ({ ...p, webhookVerifySsl: v }))} />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <FormDialog
      open
      onClose={close}
      onSubmit={submit}
      submitLabel={t('notifications.createChannel')}
      canSubmit={!!form.name.trim() && form.type !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED'}
      isSubmitting={isSubmitting}
      dirty={dirty}
      width="lg"
      aria-label={t('notifications.createChannel')}
      footerLeft={
        form.type !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED' ? (
          <button type="button" onClick={handleValidate} disabled={validating} className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40">
            {validating ? t('common.loading') : t('notifications.validateConfig')}
          </button>
        ) : undefined
      }
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        <TitleInput
          value={form.name}
          onChange={(v) => setForm((p) => ({ ...p, name: v }))}
          placeholder={t('common.name')}
          initialFocus
          onEnter={() => document.getElementById('channel-create-description')?.focus()}
          aria-label={t('common.name')}
        />
        {!!errors.name && <p className="text-sm text-destructive">{String(errors.name)}</p>}
        <AutoGrowTextarea
          id="channel-create-description"
          value={form.description}
          onChange={(v) => setForm((p) => ({ ...p, description: v }))}
          placeholder={t('notifications.templateVariables.descriptionPlaceholder')}
          minRows={1}
        />

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={form.type}
            onChange={handleChange('type')}
            fullWidth={false}
            className="w-44"
            aria-label={t('notifications.channelType')}
          >
            <option value="NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED">{t('common.select')}</option>
            {NotificationChannelTypeValues.filter((v) => v !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED').map((s) => {
              const ct = formatChannelType(s);
              return <option key={s} value={s}>{t(ct.labelKey)}</option>;
            })}
          </Select>
          <SegmentedControl
            aria-label={t('notifications.scope')}
            value={form.scope}
            onChange={(v) => setForm((p) => ({ ...p, scope: v }))}
            options={scopeOptions.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
          />
        </div>
        {!!errors.type && <p className="text-sm text-destructive">{String(errors.type)}</p>}
        {!!errors.scope && <p className="text-sm text-destructive">{String(errors.scope)}</p>}

        <div className="flex flex-wrap items-center gap-4 border-t border-border pt-2">
          <ToggleSwitch label={t('notifications.enabled')} checked={form.isEnabled} onChange={(v) => setForm((p) => ({ ...p, isEnabled: v }))} />
          <ToggleSwitch label={t('notifications.default')} checked={form.isDefault} onChange={(v) => setForm((p) => ({ ...p, isDefault: v }))} />
        </div>

        {form.type !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED' && (
          <div className="space-y-3 border-t border-border pt-3">
            {renderTypeConfig()}
          </div>
        )}

        {validationResult && (
          <Alert variant={validationResult.valid ? 'success' : 'error'} dismissible onDismiss={() => setValidationResult(null)}>
            {validationResult.valid ? t('notifications.configValid') : (validationResult.message || t('notifications.configInvalid'))}
          </Alert>
        )}

        {!!errors.form && <p className="text-sm text-destructive">{errors.form instanceof Error ? errors.form.message : t('notifications.saveError')}</p>}
      </div>
    </FormDialog>
  );
});

export default ChannelCreatePage;
